import { lazy, Suspense, useEffect, useState, type FormEvent } from 'react';
import { ApiError, apiPost } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useT } from '../i18n/LangProvider';
import { BrandMark, Icon } from '../ui/Icon';

const LoginScene = lazy(() => import('../components/LoginScene'));

const WORKSPACE_AREAS = [
  { icon: 'calendar' as const, label: 'Turni e calendario' },
  { icon: 'bell' as const, label: 'Infostaff e consegne' },
  { icon: 'book' as const, label: 'Wiki e formazione' },
];

const LOGIN_EXPERIENCES = [
  { name: 'Colosseo', meta: 'Roma · Monumenti' },
  { name: 'Castel Sant’Angelo', meta: 'Roma · Monumenti' },
  { name: 'Musei Vaticani', meta: 'Città del Vaticano · Musei' },
  { name: 'Basilica di San Pietro', meta: 'Città del Vaticano · Basilica' },
  { name: 'Fontana di Trevi', meta: 'Roma · Monumenti' },
  { name: 'Pompei', meta: 'Pompei · Archeologia' },
  { name: 'Pantheon', meta: 'Roma · Monumenti' },
  { name: 'Big Bus Tours', meta: 'Roma · Tour panoramico' },
  { name: 'Golf Cart Tours', meta: 'Roma · Tour privato' },
];

function DesktopLoginScene({
  onActiveExperienceChange,
  paused,
}: {
  onActiveExperienceChange: (index: number) => void;
  paused: boolean;
}) {
  const [visible, setVisible] = useState(() => window.matchMedia('(min-width: 881px)').matches);

  useEffect(() => {
    const query = window.matchMedia('(min-width: 881px)');
    const update = () => setVisible(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  if (!visible) return null;
  return (
    <Suspense fallback={null}>
      <LoginScene
        onActiveExperienceChange={onActiveExperienceChange}
        paused={paused}
      />
    </Suspense>
  );
}

/** Login screen — internal email/password authentication. */
export function LoginScreen() {
  const t = useT();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [forgotSent, setForgotSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeExperience, setActiveExperience] = useState(0);
  const [scenePaused, setScenePaused] = useState(false);
  const experience = LOGIN_EXPERIENCES[activeExperience] ?? LOGIN_EXPERIENCES[0];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'forgot') {
        // Always resolves the same way server-side (no email enumeration).
        await apiPost('/auth/forgot-password', { email: email.trim() });
        setForgotSent(true);
        return;
      }
      await login(email.trim(), password);
    } catch (err) {
      setError(
        mode === 'forgot'
          ? t('Invio non riuscito. Riprova.')
          : err instanceof ApiError && err.status === 401
            ? t('Email o password non validi.')
            : t('Accesso non riuscito. Riprova.'),
      );
    } finally {
      setLoading(false);
    }
  }

  const switchMode = (next: 'login' | 'forgot') => {
    setMode(next);
    setError(null);
    setForgotSent(false);
  };

  return (
    <div className="login-shell">
      <main className="login-shell__form-wrap">
        <form className="login-card" onSubmit={submit} aria-busy={loading}>
          <div className="login-card__brand">
            <span className="login-card__brand-mark">
              <BrandMark size={38} />
            </span>
            <div>
              <div className="login-card__brand-name">Touristation Hub</div>
              <div className="login-card__brand-sub">{t('Workspace aziendale')}</div>
            </div>
          </div>

          <div className="login-card__intro">
            <span className="login-card__eyebrow">{t('Accesso aziendale')}</span>
            <h1>{t('Accedi a Touristation Hub')}</h1>
            <p>
              {t('Turni, task, comunicazioni e documenti aziendali in un unico spazio.')}
            </p>
          </div>

          <div className="login-card__mobile-areas" aria-label={t('Aree principali dell’Hub')}>
            {WORKSPACE_AREAS.map((area) => (
              <span key={area.label}>
                <Icon name={area.icon} size={13} />
                {t(area.label)}
              </span>
            ))}
          </div>

          <div className="login-card__fields">
            <div className="field">
              <label className="label" htmlFor="login-email">{t('Email aziendale')}</label>
              <input
                id="login-email"
                className="input"
                type="email"
                autoComplete="username"
                inputMode="email"
                placeholder="nome@ticketstation.it"
                value={email}
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                required
                disabled={loading}
              />
            </div>

            {mode === 'login' && (
              <div className="field">
                <label className="label" htmlFor="login-password">{t('Password')}</label>
                <div className="login-password">
                  <input
                    id="login-password"
                    className="input"
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('Inserisci la password')}
                    value={password}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'login-error' : undefined}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError(null);
                    }}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="login-password__toggle"
                    onClick={() => setShowPwd(!showPwd)}
                    aria-label={showPwd ? t('Nascondi password') : t('Mostra password')}
                    aria-pressed={showPwd}
                  >
                    <Icon name={showPwd ? 'eyeOff' : 'eye'} size={16} />
                  </button>
                </div>
              </div>
            )}

            {mode === 'forgot' && (
              <p className="muted" style={{ fontSize: 13, margin: '-2px 0 2px' }}>
                {t('Inserisci la tua email: ti invieremo un link per reimpostare la password.')}
              </p>
            )}

            {error && (
              <div id="login-error" className="login-card__error" role="alert" aria-live="polite">
                <Icon name="warn" size={15} />
                <span>{error}</span>
              </div>
            )}

            {mode === 'forgot' && forgotSent && (
              <div className="login-card__error" role="status" aria-live="polite"
                style={{ background: 'var(--ok-soft)', color: 'var(--ok)', borderColor: 'var(--ok)' }}>
                <Icon name="check" size={15} />
                <span>{t('Se l’email è associata a un account, riceverai a breve il link.')}</span>
              </div>
            )}

            <button
              type="submit"
              className="btn btn--brand login-card__submit"
              disabled={loading || (mode === 'forgot' && forgotSent)}
            >
              {loading ? (
                <>{mode === 'forgot' ? t('Invio in corso') : t('Accesso in corso')}<span className="dotline"><span /><span /><span /></span></>
              ) : mode === 'forgot' ? (
                forgotSent ? <>{t('Link inviato')}</> : <>{t('Invia link di accesso')} <Icon name="send" size={15} /></>
              ) : (
                <>{t('Accedi')} <Icon name="arrowR" size={16} /></>
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: 4 }}>
              <button
                type="button"
                onClick={() => switchMode(mode === 'login' ? 'forgot' : 'login')}
                style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer', padding: 4, textDecoration: 'underline' }}
              >
                {mode === 'login' ? t('Password dimenticata?') : t('← Torna al login')}
              </button>
            </div>
          </div>

          <div className="login-card__support">
            <Icon name="shield" size={14} />
            <span>{t('Accesso riservato al team Touristation.')}</span>
          </div>

          <div className="login-card__legal">{t('Ticketstation S.r.l. · Sistema interno')}</div>
        </form>
      </main>

      <aside className="login-shell__art" aria-label="Touristation Hub">
        <div className="login-shell__art-brand">
          <span className="login-shell__art-logo"><BrandMark size={36} /></span>
          <span>Touristation · Roma</span>
        </div>

        <div className={`login-shell__art-stage${scenePaused ? ' is-paused' : ''}`}>
          <DesktopLoginScene
            onActiveExperienceChange={setActiveExperience}
            paused={scenePaused}
          />
          <button
            type="button"
            className="login-shell__scene-toggle"
            onClick={() => setScenePaused((current) => !current)}
            aria-label={scenePaused ? t('Riprendi animazione') : t('Metti in pausa animazione')}
            aria-pressed={scenePaused}
            title={scenePaused ? t('Riprendi animazione') : t('Metti in pausa animazione')}
          >
            {scenePaused ? (
              <Icon name="play" size={13} />
            ) : (
              <span className="login-shell__pause-glyph" aria-hidden="true"><i /><i /></span>
            )}
          </button>
          <div
            className="login-shell__experience-caption"
            aria-hidden="true"
            key={activeExperience}
          >
            <span className="login-shell__experience-count">
              {String(activeExperience + 1).padStart(2, '0')} / {String(LOGIN_EXPERIENCES.length).padStart(2, '0')}
            </span>
            <strong>{experience.name}</strong>
            <span className="login-shell__experience-meta">{experience.meta}</span>
            <span className="login-shell__experience-progress"><i /></span>
          </div>
        </div>

        <div className="login-shell__art-content">
          <div className="login-shell__art-kicker">
            <Icon name="sparkle" size={14} />
            Touristation · Experience network
          </div>
          <h2>{t('Roma si apre da qui.')}</h2>
          <p>
            {t('Biglietti, visite e tour per le grandi icone di Roma e Pompei, coordinati in un unico Hub.')}
          </p>
        </div>

        <div className="login-shell__art-foot">
          <span className="login-shell__status-dot" />
          {t('Workspace interno Touristation')}
          <span className="login-shell__art-city">Roma · IT</span>
        </div>
      </aside>
    </div>
  );
}
